import { getAllEnumValues } from '../../../../utils';
import { ModelEntityType } from '../../../input/types';

export enum SdgId {
  _1_No_Poverty = '1',
  _2_Zero_Hunger = '2',
  _3_Good_Health_and_Wellbeing = '3',
  _4_Quality_Education = '4',
  _5_Gender_Equality = '5',
  _6_Clean_Water_and_Sanitation = '6',
  _7_Affordable_and_Clean_Energy = '7',
  _8_Decent_Work_and_Economic_Growth = '8',
  _9_Industry_Innovation_and_Infrastructure = '9',
  _10_Reduced_Inequality = '10',
  _11_Sustainable_Cities_and_Communities = '11',
  _12_Responsible_Consumption_and_Production = '12',
  _13_Climate_Action = '13',
  _14_Life_Below_Water = '14',
  _15_Life_on_Land = '15',
  _16_Peace_Justice_and_Strong_Institutions = '16',
  _17_Partnerships_for_the_Goals = '17',
}

export enum SdgAlignmentValence {
  ALIGNED = 'aligned',
  MISALIGNED = 'misaligned',
}
export const allSdgAlignmentValences = getAllEnumValues(SdgAlignmentValence, 'string');
export enum SdgAlignmentLevel {
  STRONGLY_ALIGNED = 'STRONGLY_ALIGNED',
  ALIGNED = 'ALIGNED',
  WEAKLY_ALIGNED = 'WEAKLY_ALIGNED',
  STRONGLY_MISALIGNED = 'STRONGLY_MISALIGNED',
  MISALIGNED = 'MISALIGNED',
  WEAKLY_MISALIGNED = 'WEAKLY_MISALIGNED',
}
export const alignmentLevelsByValence: Record<
  SdgAlignmentValence,
  [SdgAlignmentLevel, SdgAlignmentLevel, SdgAlignmentLevel]
> = {
  [SdgAlignmentValence.ALIGNED]: [
    SdgAlignmentLevel.STRONGLY_ALIGNED,
    SdgAlignmentLevel.ALIGNED,
    SdgAlignmentLevel.WEAKLY_ALIGNED,
  ],
  [SdgAlignmentValence.MISALIGNED]: [
    SdgAlignmentLevel.STRONGLY_MISALIGNED,
    SdgAlignmentLevel.MISALIGNED,
    SdgAlignmentLevel.WEAKLY_MISALIGNED,
  ],
};

export const allSdgIds = getAllEnumValues(SdgId, 'string');
export const allSdgAlignmentLevels = getAllEnumValues(SdgAlignmentLevel, 'string');

export const sdgIdsInUse = allSdgIds.filter(id => id !== SdgId._17_Partnerships_for_the_Goals);

export type SdgInUse = (typeof sdgIdsInUse)[number];

export type BySdg<T> = { [sdg in SdgId]: T };
export type ByMaybeSdg<T> = Partial<BySdg<T>>;

export type BySdgAndAlignmentLevel<T> = ByMaybeSdg<ByAlignmentLevel<T>>;
export type ByAlignmentLevel<T> = {
  [alignmentLevel in SdgAlignmentLevel]?: T;
};
export type BySdgValence<T> = {
  [valence in SdgAlignmentValence]?: T;
};

export type SdgAlignmentsBySdgAndLevel = BySdgAndAlignmentLevel<SdgAlignment>;
export type SdgAlignmentByLevel = ByAlignmentLevel<SdgAlignment>;

export enum SdgUnitsMode {
  PURE_REVENUE_SHARES = 'Pure revenue shares',
  SUMMARY_PERCENTAGE = 'Summary percentage',
}

export enum SdgAlignmentSource {
  VALENCE = 'valence',
  DIRECT = 'direct',
  PARENT = 'parent',
  LEAVES = 'leaves',
  DIRECT_AND_LEAVES = 'direct-and-leaves',
  DIRECT_AND_PARENTS = 'direct-and-parents',
  MIXIN = 'mixin',
}

export interface SdgAlignment {
  /*
   * Final estimate for this (mis)alignment level.
   * - For products
   *   - This is 1 for the (mis)alignment level that corresponds to the 'total alignment' of the valence
   *   - And 0 for other (mis)alignment levels, although they may have partialAlignment, targets and items.
   *   - 'Total alignment' is calculated as a magic-level-multiplier (1, 0.5, 0.25) weighted sum of partial alignments of the valence in question.
   * - For companies, this is the revenue share ratio [0-1] of products with this (mis)alignment level
   *
   * (Before 1.2.x this was calculated as partialAlignment for products and as the revenue share of leaf products for companies.)
   */
  alignment: number;

  /*
   * Traceability field included from 1.2.x onwards for products only.
   * - For leafs and directly mapped products and mixins, this is the strongest (mis)alignment level of the product.
   * - For parent products, this is the market size weighted sum of leaf (mis)alignments for this (mis)alignemtn level.
   * - For mixins, this is the mixin weighted sum of mixed product (mis)alignments for this (mis)alignemtn level.
   */
  partialAlignment?: number;

  /*
   * Only for products and companies, i.e. not for weight sets
   * The constituents that build the partialAlignment value.
   */
  items?: AlignmentItem[];

  // Only for products, i.e. not for companies & weight sets
  source?: SdgAlignmentSource;

  /*
   * Aggregated targets contributing to this alignment level.
   * For products, the targets are aggregated within the whole valence and only for the final product alignment level (from 1.2.x onwards).
   * Present from 0.5.0 onwards
   */
  targets?: SdgTarget[];
}

export interface AlignmentItem {
  type: ModelEntityType;
  id: string;
  weight: number | undefined; // Between 0-1. Can be undefined so that we don't have to send weights to UI.
  alignment: number;
  name: string;
  targets?: SdgTarget[]; // Present from 0.5.0 onwards
}

export type MultiplierBySdgAndALignmentLevel = BySdgAndAlignmentLevel<number>;
export type MultiplierByAlignmentLevel = {
  [alignmentLevel in SdgAlignmentLevel]: number;
};

export enum SdgAlignmentStrength {
  WEAK = -1,
  NORMAL = 0,
  STRONG = 1,
}

export type DirectSdgAlignmentsBySdgAndLevel = BySdgAndAlignmentLevel<DirectSdgAlignmentWithItems>;
export type DirectSdgAlignmentsByLevel = ByAlignmentLevel<DirectSdgAlignmentWithItems>;

export interface DirectSdgAlignmentWithItems extends SdgAlignment {
  source: SdgAlignmentSource.DIRECT;
  items?: undefined;
}

export interface SdgAlignmentsWithWeightAndId {
  alignments: SdgAlignmentsBySdgAndLevel | undefined;
  weight: number;
  id: string;
}

export enum SdgTarget {
  _1_1 = '1.1',
  _1_2 = '1.2',
  _1_3 = '1.3',
  _1_4 = '1.4',
  _1_5 = '1.5',
  _2_1 = '2.1',
  _2_2 = '2.2',
  _2_3 = '2.3',
  _2_4 = '2.4',
  _2_5 = '2.5',
  _3_1 = '3.1',
  _3_2 = '3.2',
  _3_3 = '3.3',
  _3_4 = '3.4',
  _3_5 = '3.5',
  _3_6 = '3.6',
  _3_7 = '3.7',
  _3_8 = '3.8',
  _3_9 = '3.9',
  _3_b = '3.b',
  _4_1 = '4.1',
  _4_2 = '4.2',
  _4_3 = '4.3',
  _4_4 = '4.4',
  _4_5 = '4.5',
  _4_6 = '4.6',
  _4_7 = '4.7',
  _5_1 = '5.1',
  _5_2 = '5.2',
  _5_3 = '5.3',
  _5_4 = '5.4',
  _5_5 = '5.5',
  _5_6 = '5.6',
  _6_1 = '6.1',
  _6_2 = '6.2',
  _6_3 = '6.3',
  _6_4 = '6.4',
  _6_5 = '6.5',
  _6_6 = '6.6',
  _7_1 = '7.1',
  _7_2 = '7.2',
  _7_3 = '7.3',
  _8_1 = '8.1',
  _8_2 = '8.2',
  _8_3 = '8.3',
  _8_4 = '8.4',
  _8_5 = '8.5',
  _8_6 = '8.6',
  _8_7 = '8.7',
  _8_8 = '8.8',
  _8_9 = '8.9',
  _8_10 = '8.10',
  _9_1 = '9.1',
  _9_2 = '9.2',
  _9_3 = '9.3',
  _9_4 = '9.4',
  _9_5 = '9.5',
  _10_1 = '10.1',
  _10_2 = '10.2',
  _10_3 = '10.3',
  _10_4 = '10.4',
  _10_5 = '10.5',
  _10_6 = '10.6',
  _10_7 = '10.7',
  _11_1 = '11.1',
  _11_2 = '11.2',
  _11_3 = '11.3',
  _11_4 = '11.4',
  _11_5 = '11.5',
  _11_6 = '11.6',
  _11_7 = '11.7',
  _12_1 = '12.1',
  _12_2 = '12.2',
  _12_3 = '12.3',
  _12_4 = '12.4',
  _12_5 = '12.5',
  _12_6 = '12.6',
  _12_7 = '12.7',
  _12_8 = '12.8',
  _13_1 = '13.1',
  _13_2 = '13.2',
  _13_3 = '13.3',
  _14_1 = '14.1',
  _14_2 = '14.2',
  _14_3 = '14.3',
  _14_4 = '14.4',
  _15_1 = '15.1',
  _15_2 = '15.2',
  _15_3 = '15.3',
  _15_4 = '15.4',
  _15_5 = '15.5',
  _15_6 = '15.6',
  _15_7 = '15.7',
  _15_8 = '15.8',
  _15_9 = '15.9',
  _16_1 = '16.1',
  _16_2 = '16.2',
  _16_3 = '16.3',
  _16_4 = '16.4',
  _16_5 = '16.5',
  _16_6 = '16.6',
  _16_7 = '16.7',
  _16_8 = '16.8',
  _16_9 = '16.9',
  _16_10 = '16.10',
  _17_1 = '17.1',
  _17_2 = '17.2',
  _17_3 = '17.3',
  _17_4 = '17.4',
  _17_5 = '17.5',
  _17_6 = '17.6',
  _17_7 = '17.7',
  _17_8 = '17.8',
  _17_9 = '17.9',
  _17_10 = '17.10',
  _17_11 = '17.11',
  _17_12 = '17.12',
  _17_13 = '17.13',
  _17_14 = '17.14',
  _17_15 = '17.15',
  _17_16 = '17.16',
  _17_17 = '17.17',
  _17_19 = '17.19',
  _17_18 = '17.18',
}

// Needs to be in common because of RenderView
export type SdgAlignmentsLayout = 'normal' | 'tight';

export const allSdgTargets = Object.values(SdgTarget);

export type SdgEntityType =
  | ModelEntityType.COMPANY
  | ModelEntityType.GROUP
  | ModelEntityType.PORTFOLIO
  | ModelEntityType.PRODUCT;

export type DerivedSdgAlignments = {
  summaryPercentages: SummaryPercentages;
  pureRevenueShares: PureRevenueShares;
  aggregatedPureRevenueShares: AggregatedPureRevenueShares;
};

export type SummaryPercentages = ByMaybeSdg<BySdgValence<number>>;
export type PureRevenueShares = ByMaybeSdg<ByAlignmentLevel<number>>;
export type AggregatedPureRevenueShares = {
  // Make branching of read logic simpler in the future if the schema changes in a non-backwards compatible way
  version: 1;
  values:
    | {
        [key in AggregatedSdgAlignmentKey]: number;
      }
    | Record<string, never>; // implies missing values
};

export const aggregatedSdgAlignmentKeys = [
  'any_alignment_moderate_or_stronger',
  'any_misalignment_moderate_or_stronger',
  'any_alignment_moderate_or_stronger_strict',
  'any_alignment_strong',
  'any_misalignment_strong',
  'any_alignment_strong_strict',
  'soc_alignment_moderate_or_stronger',
  'soc_misalignment_moderate_or_stronger',
  'soc_alignment_moderate_or_stronger_strict',
  'soc_alignment_strong',
  'soc_misalignment_strong',
  'soc_alignment_strong_strict',
  'env_alignment_moderate_or_stronger',
  'env_misalignment_moderate_or_stronger',
  'env_alignment_moderate_or_stronger_strict',
  'env_alignment_strong',
  'env_misalignment_strong',
  'env_alignment_strong_strict',
] as const;

export type AggregatedSdgAlignmentKey = (typeof aggregatedSdgAlignmentKeys)[number];
